import { createSlice, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';

import { rootReducer } from '@verantos-dev/core-annotation-tool';
import { Span } from '@verantos-dev/core-annotation-tool';
import type { HighlightedTextNew, SelectedSpan } from '@verantos-dev/core-annotation-tool';

import { SUPERVISOR_REVIEW_TAB_OPTIONS } from '../constants';

export interface SupervisorState {
  filterBy: string;
  noteSortBy: string;
  groupBy: string;
  selectedNoteId: string | null;
  annotatorOneAllConceptsExpanded: boolean | undefined;
  annotatorTwoAllConceptsExpanded: boolean | undefined;
  conflictsAllConceptsExpanded: boolean | undefined;
  resolvedAllConceptsExpanded: boolean | undefined;
  annotatorOneExpandedConceptIds: string[];
  annotatorTwoExpandedConceptIds: string[];
  conflictExpandedConceptIds: string[];
  resolvedExpandedConceptIds: string[];
  conflictIdsInReplaceMode: { conflictId: string; annotationIdsToReject: string[] }[];
  resolvedAnnotationIdsInReplaceMode: { conceptId: string; annotationIdToDelete: string }[];
  annotationIdsInEditMode: string[];
  addEvidenceModeForConceptId: string | null;
  highlightedTextSpans: HighlightedTextNew[];
  pendingSpans: Record<string, Span[]>;
  viewOptionExpanded: boolean;
  reviewTabSelected: string;
  deleteSpanError: Record<string, string | null>;
  createAnnotationError: Record<string, string | null>;
  editAnnotationError: Record<string, string | null>;
  conflictAcceptError: Record<string, string | null>;
  conflictRejectError: Record<string, string | null>;
  deleteResolvedAnnotationError: Record<string, string | null>;
  markSupervisionCompleteError: string | null;
  showAnnotations: boolean;
  evidenceIdsToDelete: Record<string, string[]>;
  finalizedConceptIdsInCreateMode: string[];
  dirtyNewAnnotationConceptIds: string[];
}

const initialState: SupervisorState = {
  filterBy: 'all',
  noteSortBy: 'ascending',
  groupBy: 'none',
  selectedNoteId: null,
  annotatorOneAllConceptsExpanded: undefined,
  annotatorTwoAllConceptsExpanded: undefined,
  conflictsAllConceptsExpanded: undefined,
  resolvedAllConceptsExpanded: undefined,
  annotatorOneExpandedConceptIds: [],
  annotatorTwoExpandedConceptIds: [],
  conflictExpandedConceptIds: [],
  resolvedExpandedConceptIds: [],
  annotationIdsInEditMode: [],
  addEvidenceModeForConceptId: null,
  highlightedTextSpans: [],
  pendingSpans: {},
  viewOptionExpanded: false,
  reviewTabSelected: SUPERVISOR_REVIEW_TAB_OPTIONS[0],
  conflictIdsInReplaceMode: [],
  resolvedAnnotationIdsInReplaceMode: [],
  deleteSpanError: {},
  createAnnotationError: {},
  editAnnotationError: {},
  conflictAcceptError: {},
  conflictRejectError: {},
  deleteResolvedAnnotationError: {},
  markSupervisionCompleteError: null,
  showAnnotations: false,
  evidenceIdsToDelete: {},
  finalizedConceptIdsInCreateMode: [],
  dirtyNewAnnotationConceptIds: [],
};

export const supervisorSlice = createSlice({
  name: 'supervisor',
  initialState,
  reducers: {
    setFilterBy: (state, action: PayloadAction<string>) => {
      state.filterBy = action.payload;
    },
    setNoteSortBy: (state, action: PayloadAction<string>) => {
      state.noteSortBy = action.payload;
    },
    clearNoteSortBy: (state) => {
      state.noteSortBy = 'ascending';
    },
    clearFilterBy: (state) => {
      state.filterBy = 'all';
    },
    setGroupBy: (state, action: PayloadAction<string>) => {
      state.groupBy = action.payload;
    },
    clearGroupBy: (state) => {
      state.groupBy = 'none';
    },
    setSelectedNoteId: (state, action: PayloadAction<string | null>) => {
      state.selectedNoteId = action.payload;
    },
    clearSelectedNoteId: (state) => {
      state.selectedNoteId = null;
    },
    addToAnnotatorOneExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (state.annotatorOneExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.annotatorOneExpandedConceptIds.push(action.payload);
    },
    addToAnnotatorTwoExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (state.annotatorTwoExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.annotatorTwoExpandedConceptIds.push(action.payload);
    },
    removeFromAnnotatorOneExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (!state.annotatorOneExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.annotatorOneExpandedConceptIds = state.annotatorOneExpandedConceptIds.filter(
        (id) => id !== action.payload
      );
    },
    removeFromAnnotatorTwoExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (!state.annotatorTwoExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.annotatorTwoExpandedConceptIds = state.annotatorTwoExpandedConceptIds.filter(
        (id) => id !== action.payload
      );
    },
    setAnnotatorOneAllConceptsExpanded: (state, action: PayloadAction<boolean>) => {
      state.annotatorOneAllConceptsExpanded = action.payload;
    },
    setAnnotatorTwoAllConceptsExpanded: (state, action: PayloadAction<boolean>) => {
      state.annotatorTwoAllConceptsExpanded = action.payload;
    },
    clearAnnotatorOneAllConceptsExpanded: (state) => {
      state.annotatorOneAllConceptsExpanded = undefined;
    },
    clearAnnotatorTwoAllConceptsExpanded: (state) => {
      state.annotatorTwoAllConceptsExpanded = undefined;
    },
    setConflictsAllConceptsExpanded: (state, action: PayloadAction<boolean>) => {
      state.conflictsAllConceptsExpanded = action.payload;
    },
    clearConflictsAllConceptsExpanded: (state) => {
      state.conflictsAllConceptsExpanded = undefined;
    },
    setResolvedAllConceptsExpanded: (state, action: PayloadAction<boolean>) => {
      state.resolvedAllConceptsExpanded = action.payload;
    },
    clearResolvedAllConceptsExpanded: (state) => {
      state.resolvedAllConceptsExpanded = undefined;
    },
    addToConflictExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (state.conflictExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.conflictExpandedConceptIds.push(action.payload);
    },
    removeFromConflictExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (!state.conflictExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.conflictExpandedConceptIds = state.conflictExpandedConceptIds.filter(
        (id) => id !== action.payload
      );
    },
    addToResolvedExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (state.resolvedExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.resolvedExpandedConceptIds.push(action.payload);
    },
    removeFromResolvedExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (!state.resolvedExpandedConceptIds.includes(action.payload)) {
        return;
      }
      state.resolvedExpandedConceptIds = state.resolvedExpandedConceptIds.filter(
        (id) => id !== action.payload
      );
    },
    addToConflictIdsInReplaceMode: (
      state,
      action: PayloadAction<{ conflictId: string; annotationIdsToReject: string[] }>
    ) => {
      if (
        state.conflictIdsInReplaceMode.some(
          (entry) => entry.conflictId === action.payload.conflictId
        )
      ) {
        return;
      }
      state.conflictIdsInReplaceMode.push(action.payload);
    },
    removeFromConflictIdsInReplaceMode: (state, action: PayloadAction<string>) => {
      if (!state.conflictIdsInReplaceMode.some((entry) => entry.conflictId === action.payload)) {
        return;
      }
      state.conflictIdsInReplaceMode = state.conflictIdsInReplaceMode.filter(
        (entry) => entry.conflictId !== action.payload
      );
    },
    clearConflictIdsInReplaceMode: (state) => {
      state.conflictIdsInReplaceMode = [];
    },
    addToResolvedAnnotationIdsInReplaceMode: (
      state,
      action: PayloadAction<{ conceptId: string; annotationIdToDelete: string }>
    ) => {
      if (
        state.resolvedAnnotationIdsInReplaceMode.some(
          (entry) => entry.conceptId === action.payload.conceptId
        )
      ) {
        return;
      }
      state.resolvedAnnotationIdsInReplaceMode.push(action.payload);
    },
    removeFromResolvedAnnotationIdsInReplaceMode: (state, action: PayloadAction<string>) => {
      state.resolvedAnnotationIdsInReplaceMode = state.resolvedAnnotationIdsInReplaceMode.filter(
        (entry) => entry.conceptId !== action.payload
      );
    },
    addToAnnotationIdsInEditMode: (state, action: PayloadAction<string>) => {
      if (state.annotationIdsInEditMode.includes(action.payload)) {
        return;
      }
      state.annotationIdsInEditMode.push(action.payload);
    },
    removeFromAnnotationIdsInEditMode: (state, action: PayloadAction<string>) => {
      if (!state.annotationIdsInEditMode.includes(action.payload)) {
        return;
      }
      state.annotationIdsInEditMode = state.annotationIdsInEditMode.filter(
        (id) => id !== action.payload
      );
    },
    clearConceptAnnotationIdsInEditMode: (state) => {
      state.annotationIdsInEditMode = [];
    },
    setAddEvidenceModeForConceptId: (state, action: PayloadAction<string>) => {
      state.addEvidenceModeForConceptId = action.payload;
    },
    clearAddEvidenceModeForConceptId: (state) => {
      state.addEvidenceModeForConceptId = null;
    },
    setHighlightedTextSpans: (state, action: PayloadAction<HighlightedTextNew[]>) => {
      state.highlightedTextSpans = action.payload;
    },
    addHighlightedTextSpan: (state, action: PayloadAction<SelectedSpan & { number?: number }>) => {
      if (
        state.highlightedTextSpans.some(
          (ht) =>
            ht.offsetStart === action.payload.startOffset &&
            ht.offsetEnd === action.payload.endOffset &&
            ht.noteId === action.payload.noteId &&
            ht.annotationId === action.payload.annotationId
        )
      ) {
        return;
      }
      // Calculate the number based on existing spans for this annotation
      // If number is provided, use it; otherwise calculate it
      let number = action.payload.number;
      if (number === undefined) {
        // Count existing spans for this annotation
        const existingSpansForAnnotation = state.highlightedTextSpans.filter(
          (ht) => ht.annotationId === action.payload.annotationId
        );
        // Count pending spans for this annotation
        const pendingSpansForAnnotation = state.pendingSpans[action.payload.annotationId] || [];
        // Total count = existing highlighted spans + pending spans + 1 (for this new one)
        number = existingSpansForAnnotation.length + pendingSpansForAnnotation.length + 1;
      }
      state.highlightedTextSpans.push({
        number,
        offsetStart: action.payload.startOffset,
        offsetEnd: action.payload.endOffset,
        noteId: action.payload.noteId,
        annotationId: action.payload.annotationId,
        text: action.payload.text,
      });
    },

    // Add a single span to highlightedTextSpans (for "Show Evidence" button)
    addHighlightedTextSpanFromSpan: (
      state,
      action: PayloadAction<{ span: Span; number: number; annotationId: string }>
    ) => {
      const { span, number, annotationId } = action.payload;
      if (
        state.highlightedTextSpans.some(
          (ht) =>
            ht.offsetStart === span.offsetStart &&
            ht.offsetEnd === span.offsetEnd &&
            ht.noteId === span.noteId &&
            ht.annotationId === annotationId
        )
      ) {
        return;
      }
      state.highlightedTextSpans.push({
        number,
        offsetStart: span.offsetStart,
        offsetEnd: span.offsetEnd,
        noteId: span.noteId,
        annotationId,
        text: span.text,
      });
    },
    removeHighlightedTextSpan: (
      state,
      action: PayloadAction<{
        offsetStart: number;
        offsetEnd: number;
        noteId: string;
        annotationId: string;
      }>
    ) => {
      state.highlightedTextSpans = state.highlightedTextSpans.filter(
        (ht) =>
          !(
            ht.offsetStart === action.payload.offsetStart &&
            ht.offsetEnd === action.payload.offsetEnd &&
            ht.noteId === action.payload.noteId &&
            ht.annotationId === action.payload.annotationId
          )
      );
      // Renumber remaining spans for this annotation in insertion order
      state.highlightedTextSpans
        .filter((ht) => ht.annotationId === action.payload.annotationId)
        .forEach((ht, i) => {
          ht.number = i + 1;
        });
    },
    clearHighlightedTextSpans: (state) => {
      state.highlightedTextSpans = [];
    },
    // Pending spans management
    addPendingSpan: (state, action: PayloadAction<{ annotationId: string; span: Span }>) => {
      const { annotationId, span } = action.payload;
      if (!state.pendingSpans[annotationId]) {
        state.pendingSpans[annotationId] = [];
      }
      // Check if span already exists (same noteId, offsetStart, offsetEnd)
      const exists = state.pendingSpans[annotationId].some(
        (s) =>
          s.noteId === span.noteId &&
          s.offsetStart === span.offsetStart &&
          s.offsetEnd === span.offsetEnd
      );
      if (!exists) {
        state.pendingSpans[annotationId].push(span);
      }
    },
    removePendingSpan: (
      state,
      action: PayloadAction<{
        annotationId: string;
        noteId: string;
        offsetStart: number;
        offsetEnd: number;
      }>
    ) => {
      const { annotationId, noteId, offsetStart, offsetEnd } = action.payload;
      if (state.pendingSpans[annotationId]) {
        state.pendingSpans[annotationId] = state.pendingSpans[annotationId].filter(
          (s) =>
            !(s.noteId === noteId && s.offsetStart === offsetStart && s.offsetEnd === offsetEnd)
        );
        // Clean up empty arrays
        if (state.pendingSpans[annotationId].length === 0) {
          delete state.pendingSpans[annotationId];
        }
      }
    },
    clearPendingSpansForAnnotation: (state, action: PayloadAction<string>) => {
      delete state.pendingSpans[action.payload];
    },
    setViewOptionExpanded: (state, action: PayloadAction<boolean>) => {
      state.viewOptionExpanded = action.payload;
    },
    clearViewOptionExpanded: (state) => {
      state.viewOptionExpanded = false;
    },
    setDeleteSpanError: (
      state,
      action: PayloadAction<{ annotationId: string; error: string | null }>
    ) => {
      state.deleteSpanError[action.payload.annotationId] = action.payload.error;
    },
    clearDeleteSpanError: (state) => {
      state.deleteSpanError = {};
    },
    setCreateAnnotationError: (
      state,
      action: PayloadAction<{ conceptId: string; error: string | null }>
    ) => {
      state.createAnnotationError[action.payload.conceptId] = action.payload.error;
    },
    clearCreateAnnotationError: (state) => {
      state.createAnnotationError = {};
    },
    setEditAnnotationError: (
      state,
      action: PayloadAction<{ annotationId: string; error: string | null }>
    ) => {
      state.editAnnotationError[action.payload.annotationId] = action.payload.error;
    },
    clearEditAnnotationError: (state) => {
      state.editAnnotationError = {};
    },
    setConflictAcceptError: (
      state,
      action: PayloadAction<{ conflictId: string; error: string | null }>
    ) => {
      state.conflictAcceptError[action.payload.conflictId] = action.payload.error;
    },
    clearConflictAcceptError: (state, action: PayloadAction<string>) => {
      state.conflictAcceptError[action.payload] = null;
    },
    setConflictRejectError: (
      state,
      action: PayloadAction<{ conflictId: string; error: string | null }>
    ) => {
      state.conflictRejectError[action.payload.conflictId] = action.payload.error;
    },
    clearConflictRejectError: (state, action: PayloadAction<string>) => {
      state.conflictRejectError[action.payload] = null;
    },
    setDeleteResolvedAnnotationError: (
      state,
      action: PayloadAction<{ annotationId: string; error: string | null }>
    ) => {
      state.deleteResolvedAnnotationError[action.payload.annotationId] = action.payload.error;
    },
    clearDeleteResolvedAnnotationError: (state) => {
      state.deleteResolvedAnnotationError = {};
    },
    setMarkSupervisionCompleteError: (state, action: PayloadAction<string | null>) => {
      state.markSupervisionCompleteError = action.payload;
    },
    clearMarkSupervisionCompleteError: (state) => {
      state.markSupervisionCompleteError = null;
    },
    addToEvidenceIdsToDelete: (
      state,
      action: PayloadAction<{ annotationId: string; evidenceId: string }>
    ) => {
      const { annotationId, evidenceId } = action.payload;
      if (state.evidenceIdsToDelete[annotationId]) {
        state.evidenceIdsToDelete[annotationId].push(evidenceId);
      } else {
        state.evidenceIdsToDelete[annotationId] = [evidenceId];
      }
    },
    clearEvidenceIdsToDelete: (
      state,
      action: PayloadAction<{ annotationId?: string } | undefined>
    ) => {
      const annotationId = action.payload?.annotationId;
      if (annotationId) {
        delete state.evidenceIdsToDelete[annotationId];
      } else {
        state.evidenceIdsToDelete = {};
      }
    },
    setShowAnnotations: (state) => {
      state.showAnnotations = true;
    },
    clearShowAnnotations: (state) => {
      state.showAnnotations = false;
    },
    setReviewTabSelected: (state, action: PayloadAction<string>) => {
      state.reviewTabSelected = action.payload;
    },
    clearReviewTabSelected: (state) => {
      state.reviewTabSelected = SUPERVISOR_REVIEW_TAB_OPTIONS[0];
    },
    addToFinalizedConceptIdsInCreateMode: (state, action: PayloadAction<string>) => {
      if (state.finalizedConceptIdsInCreateMode.includes(action.payload)) return;
      state.finalizedConceptIdsInCreateMode.push(action.payload);
    },
    removeFromFinalizedConceptIdsInCreateMode: (state, action: PayloadAction<string>) => {
      state.finalizedConceptIdsInCreateMode = state.finalizedConceptIdsInCreateMode.filter(
        (id) => id !== action.payload
      );
    },
    setNewAnnotationFormDirty: (state, action: PayloadAction<string>) => {
      if (!state.dirtyNewAnnotationConceptIds.includes(action.payload)) {
        state.dirtyNewAnnotationConceptIds.push(action.payload);
      }
    },
    clearNewAnnotationFormDirty: (state, action: PayloadAction<string>) => {
      state.dirtyNewAnnotationConceptIds = state.dirtyNewAnnotationConceptIds.filter(
        (id) => id !== action.payload
      );
    },
  },
});

export const {
  setFilterBy,
  clearFilterBy,
  setNoteSortBy,
  clearNoteSortBy,
  setGroupBy,
  clearGroupBy,
  setSelectedNoteId,
  clearSelectedNoteId,
  addToAnnotatorOneExpandedConceptIds,
  addToAnnotatorTwoExpandedConceptIds,
  removeFromAnnotatorOneExpandedConceptIds,
  removeFromAnnotatorTwoExpandedConceptIds,
  setAnnotatorOneAllConceptsExpanded,
  setAnnotatorTwoAllConceptsExpanded,
  clearAnnotatorOneAllConceptsExpanded,
  clearAnnotatorTwoAllConceptsExpanded,
  setConflictsAllConceptsExpanded,
  clearConflictsAllConceptsExpanded,
  setResolvedAllConceptsExpanded,
  clearResolvedAllConceptsExpanded,
  addHighlightedTextSpanFromSpan,
  clearHighlightedTextSpans,
  addToConflictExpandedConceptIds,
  removeFromConflictExpandedConceptIds,
  addToResolvedExpandedConceptIds,
  removeFromResolvedExpandedConceptIds,
  addToConflictIdsInReplaceMode,
  removeFromConflictIdsInReplaceMode,
  clearConflictIdsInReplaceMode,
  addPendingSpan,
  removePendingSpan,
  clearPendingSpansForAnnotation,
  setAddEvidenceModeForConceptId,
  clearAddEvidenceModeForConceptId,
  setHighlightedTextSpans,
  addHighlightedTextSpan,
  removeHighlightedTextSpan,
  setViewOptionExpanded,
  clearViewOptionExpanded,
  setShowAnnotations,
  clearShowAnnotations,
  setReviewTabSelected,
  clearReviewTabSelected,
  setDeleteSpanError,
  clearDeleteSpanError,
  setCreateAnnotationError,
  clearCreateAnnotationError,
  setEditAnnotationError,
  clearEditAnnotationError,
  setConflictAcceptError,
  clearConflictAcceptError,
  setConflictRejectError,
  clearConflictRejectError,
  setDeleteResolvedAnnotationError,
  clearDeleteResolvedAnnotationError,
  setMarkSupervisionCompleteError,
  clearMarkSupervisionCompleteError,
  addToAnnotationIdsInEditMode,
  removeFromAnnotationIdsInEditMode,
  clearConceptAnnotationIdsInEditMode,
  addToEvidenceIdsToDelete,
  clearEvidenceIdsToDelete,
  addToFinalizedConceptIdsInCreateMode,
  removeFromFinalizedConceptIdsInCreateMode,
  addToResolvedAnnotationIdsInReplaceMode,
  removeFromResolvedAnnotationIdsInReplaceMode,
  setNewAnnotationFormDirty,
  clearNewAnnotationFormDirty,
} = supervisorSlice.actions;

declare module '@verantos-dev/core-annotation-tool' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof supervisorSlice> {}
}

supervisorSlice.injectInto(rootReducer);
